import { useCallback, useReducer } from "react";
import { useSelector, useDispatch } from "react-redux";
import { orderTypeIds } from "../../../../shared/constants/order";
import * as storeRepo from "../../../../shared/repos/graphql/store";
import * as elementsActions from "../../../../redux/actions/elements";
import { setCaptchaToken } from "../../../../shared/utilities/captcha";
import captchaAction from "../../../../shared/constants/captchaAction";
import httpStatusConstant from "../../../../shared/constants/httpStatus";
import elementConstants from "../../../../shared/constants/element";

/**
 * @typedef {Object} FetchLocationParams
 * @property {number|string} lat
 * @property {number|string} lng
 * @property {string} externalId
 * @property {number|undefined} orderTypeId
 */

/**
 * @typedef {Object} Day
 * @property {string} day
 * @property {string} hour
 */

/**
 * @typedef {Object} Hour
 * @property {string} type
 * @property {Day[]} days
 */

/**
 * @typedef {Object} Store
 * @property {string} id
 * @property {string} countryId
 * @property {string} name
 * @property {string} slug
 * @property {string} address
 * @property {string} mailingAddress
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {string} distanceToStore
 * @property {string} phone
 * @property {string} storefrontImage
 * @property {number} lat
 * @property {number} lng
 * @property {boolean} inDeliveryRange
 * @property {string} boundary
 * @property {string} status
 * @property {string} note
 * @property {string} storeType
 * @property {boolean} isPickupOpen
 * @property {boolean} isDeliveryOpen
 * @property {boolean} showContactless
 * @property {Hour[]} hours
 * @property {string} blurb
 * @property {string} promotionalText
 */

/**
 * Checks if an error is a 401 Unauthorized error
 * @param {Error} error
 * @returns {boolean}
 */
const isUnauthorizedError = error => {
  const errorMessage = error?.message || error?.toString() || "";
  const networkError = error?.networkError;
  const statusCode = networkError?.statusCode;
  return (
    statusCode === 401 ||
    errorMessage.indexOf(httpStatusConstant.unauthorised) !== -1 ||
    (networkError?.result &&
      JSON.stringify(networkError.result).indexOf(
        httpStatusConstant.unauthorised
      ) !== -1)
  );
};

/**
 * @typedef {Object} SearchStores
 * @property {string} value
 * @property {Error | null} error
 * @property {boolean} fetched
 * @property {boolean} loading
 * @property {Array<Store>} stores
 * @property {boolean} showCaptchaChallenge
 * @property {(params: FetchLocationParams) => Promise<void>} fetch
 * @property {(token: string) => void} onCaptchaVerify
 *
 * @returns {SearchStores}
 */
export default function useSearchStores() {
  const dispatch = useDispatch();
  const useRecaptcha = useSelector(state => state.elements.useRecaptcha);

  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      value: "",
      error: null,
      fetched: false,
      loading: false,
      stores: undefined,
      showCaptchaChallenge: false,
      skipGenerateNewToken: false,
      lastFetchParams: null
    }
  );

  const setVerificationToken = value =>
    dispatch(elementsActions.setVerificationToken(value));
  const setVerificationAction = value =>
    dispatch(elementsActions.setVerificationAction(value));
  const setVerificationProvider = value =>
    dispatch(elementsActions.setVerificationProvider(value));

  const fetchStores = useCallback(
    /**
     * @param {FetchLocationParams} params
     * @param {boolean} skipCaptchaGeneration - Skip generating new captcha token (used after challenge)
     */
    async (
      { lat, lng, externalId, orderTypeId = orderTypeIds.pickup },
      skipCaptchaGeneration = false
    ) => {
      try {
        setState({
          loading: true,
          error: null,
          showCaptchaChallenge: false,
          lastFetchParams: { lat, lng, externalId, orderTypeId }
        });

        // Generate captcha token before request if needed
        if (useRecaptcha === 1 && !skipCaptchaGeneration) {
          await setCaptchaToken(
            setVerificationToken,
            setVerificationAction,
            captchaAction.addressSearching
          );
        }

        const result = await storeRepo.getStores({
          lat,
          lng,
          externalId,
          orderTypeId
        });
        setState({
          stores: result.data.storeSearch.stores,
          skipGenerateNewToken: false
        });
        setVerificationProvider("");
      } catch (error) {
        // Check if it's a 401 error and captcha is enabled
        if (isUnauthorizedError(error) && useRecaptcha === 1) {
          setState({
            error,
            showCaptchaChallenge: true
          });
        } else {
          setState({ error });
        }
      } finally {
        setState({ loading: false, fetched: true });
      }
    },
    [
      useRecaptcha,
      setVerificationToken,
      setVerificationAction,
      setVerificationProvider
    ]
  );

  /**
   * Called after user completes captcha challenge
   * @param {string} token - The captcha token from the challenge
   */
  const onCaptchaVerify = useCallback(
    token => {
      if (token) {
        setVerificationToken(token);
        setVerificationProvider(elementConstants.challengeRecaptcha);
        setState({
          showCaptchaChallenge: false,
          skipGenerateNewToken: true
        });

        // Retry the last fetch with the challenge token
        if (state.lastFetchParams) {
          fetchStores(state.lastFetchParams, true);
        }
      }
    },
    [
      state.lastFetchParams,
      fetchStores,
      setVerificationToken,
      setVerificationProvider
    ]
  );

  return {
    ...state,
    fetch: fetchStores,
    onCaptchaVerify
  };
}
